import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { taskService } from '../../../services/taskService';
import { teamService } from '../../../services/teamService';
import { useSubscription } from '../../../contexts/SubscriptionContext';


const TeamScheduleGantt = () => {
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [viewMode, setViewMode] = useState('day');
  const [teamTasks, setTeamTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const { hasPremium, needsUpgrade } = useSubscription();

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    assignedTo: '',
    locationName: '',
    roomType: 'grow_room',
    startTime: '',
    endTime: '',
    priority: 'medium'
  });

  const dragOverRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [tasks, members] = await Promise.all([
        taskService?.getTasksByDate(selectedDate),
        teamService?.getTeamMembers()
      ]);

      // If no real team members (demo mode), create demo members
      const effectiveMembers = members?.length > 0 ? members : [
        {
          userId: 'demo-user-1',
          userProfile: { fullName: 'Sarah Johnson', email: 'sarah@demo.com' },
          position: 'IPM Specialist',
          department: 'IPM Operations'
        },
        {
          userId: 'demo-user-2',
          userProfile: { fullName: 'Mike Chen', email: 'mike@demo.com' },
          position: 'Grow Technician',
          department: 'Cultivation'
        },
        {
          userId: 'demo-user-3',
          userProfile: { fullName: 'Emily Davis', email: 'emily@demo.com' },
          position: 'Cultivation Lead',
          department: 'Cultivation'
        }
      ];

      // Group tasks by team member
      const memberTaskMap = {};
      
      effectiveMembers?.forEach(member => {
        memberTaskMap[member?.userId] = {
          id: member?.userId,
          member: member?.userProfile?.fullName || member?.userProfile?.email || 'Unknown',
          avatar: `https://images.unsplash.com/photo-1${Math.floor(Math.random() * 900000000)}-${Math.floor(Math.random() * 900000000)}?auto=format&fit=crop&w=64&h=64`,
          avatarAlt: `${member?.userProfile?.fullName || 'Team member'} profile photo showing professional headshot`,
          tasks: [],
          position: member?.position,
          department: member?.department
        };
      });

      tasks?.forEach(task => {
        const assignedUserId = task?.assignedTo;
        if (assignedUserId && memberTaskMap?.[assignedUserId]) {
          memberTaskMap?.[assignedUserId]?.tasks?.push({
            id: task?.id,
            title: task?.title,
            startTime: task?.startTime,
            endTime: task?.endTime,
            status: task?.status,
            priority: task?.priority,
            location: task?.locationName || task?.location,
            roomType: task?.roomType,
            taskType: task?.taskType
          });
        }
      });

      setTeamTasks(Object.values(memberTaskMap));
      setTeamMembers(effectiveMembers);

    } catch (err) {
      console.error('Error loading team schedule:', err);
      setError(err?.message || 'Failed to load team schedule');
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = Array.from({ length: 10 }, (_, i) => {
    const hour = 7 + i;
    return `${hour?.toString()?.padStart(2, '0')}:00`;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':return 'bg-success text-success-foreground';
      case 'in_progress':return 'bg-warning text-warning-foreground';
      case 'pending':return 'bg-error text-error-foreground';
      case 'scheduled':return 'bg-primary text-primary-foreground';
      default:return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':return 'AlertTriangle';
      case 'medium':return 'Clock';
      case 'low':return 'CheckCircle';
      default:return 'Circle';
    }
  };

  const calculateTaskPosition = (startTime, endTime) => {
    const startHour = parseInt(startTime?.split(':')?.[0]);
    const startMinute = parseInt(startTime?.split(':')?.[1]);
    const endHour = parseInt(endTime?.split(':')?.[0]);
    const endMinute = parseInt(endTime?.split(':')?.[1]);

    const startPosition = ((startHour - 7) * 60 + startMinute) / 60;
    const duration = ((endHour - 7) * 60 + endMinute - (startHour - 7) * 60 - startMinute) / 60;

    return {
      left: `${Math.max(0, startPosition / 10 * 100)}%`,
      width: `${Math.max(5, duration / 10 * 100)}%`
    };
  };

  const handleTaskDragStart = (e, task, memberId) => {
    // Premium feature - drag and drop reassignment
    if (needsUpgrade) {
      e?.preventDefault();
      return;
    }
    setDraggedTask({ ...task, originalMemberId: memberId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTaskDragEnd = () => {
    setDraggedTask(null);
  };

  const handleMemberDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleMemberDrop = async (e, targetMemberId) => {
    e?.preventDefault();
    
    // Premium feature - drag and drop reassignment
    if (needsUpgrade) {
      return;
    }
    
    if (!draggedTask || draggedTask?.originalMemberId === targetMemberId) {
      return;
    }

    try {
      // Find the target member info
      const targetMember = teamMembers?.find(m => m?.userId === targetMemberId);
      if (!targetMember) return;

      // Update task assignment
      await taskService?.updateTask(draggedTask?.id, {
        assignedTo: targetMemberId
      });

      // Reload data to reflect changes
      await loadData();

    } catch (err) {
      console.error('Error reassigning task:', err);
      setError(err?.message || 'Failed to reassign task');
    }
  };

  const handleTaskClick = (task, memberId) => {
    // Premium feature - editing tasks
    if (needsUpgrade) {
      return;
    }

    const member = teamMembers?.find(m => m?.userId === memberId);
    setEditingTask({ ...task, memberId, memberName: member?.userProfile?.fullName });
    setEditForm({
      title: task?.title,
      assignedTo: memberId,
      locationName: task?.location,
      roomType: task?.roomType || 'grow_room',
      startTime: task?.startTime,
      endTime: task?.endTime,
      priority: task?.priority
    });
    setShowTaskModal(true);
  };

  const handleSaveTask = async () => {
    if (!editingTask) return;

    try {
      const updates = {
        title: editForm?.title,
        assignedTo: editForm?.assignedTo,
        locationName: editForm?.locationName,
        roomType: editForm?.roomType,
        priority: editForm?.priority,
        // Calculate new due date based on start time
        dueDate: new Date(`${selectedDate}T${editForm.startTime}:00`)?.toISOString()
      };

      await taskService?.updateTask(editingTask?.id, updates);
      
      setShowTaskModal(false);
      setEditingTask(null);
      await loadData();

    } catch (err) {
      console.error('Error updating task:', err);
      setError(err?.message || 'Failed to update task');
    }
  };

  const handleCreateNewTask = () => {
    setEditingTask(null);
    setEditForm({
      title: '',
      assignedTo: teamMembers?.[0]?.userId || '',
      locationName: "Sarah's New Grow Room",
      roomType: 'grow_room',
      startTime: '09:00',
      endTime: '11:00',
      priority: 'medium'
    });
    setShowTaskModal(true);
  };

  const handleCreateTask = async () => {
    try {
      const taskData = {
        title: editForm?.title,
        assignedTo: editForm?.assignedTo,
        locationName: editForm?.locationName,
        roomType: editForm?.roomType,
        priority: editForm?.priority,
        status: 'pending',
        taskType: 'cultivation',
        dueDate: new Date(`${selectedDate}T${editForm.startTime}:00`)?.toISOString(),
        estimatedDuration: calculateDuration(editForm?.startTime, editForm?.endTime)
      };

      await taskService?.createTask(taskData);
      
      setShowTaskModal(false);
      await loadData();

    } catch (err) {
      console.error('Error creating task:', err);
      setError(err?.message || 'Failed to create task');
    }
  };

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(`2000-01-01T${startTime}:00`);
    const end = new Date(`2000-01-01T${endTime}:00`);
    return Math.max(30, (end - start) / (1000 * 60)); // Minimum 30 minutes
  };

  const roomTypeOptions = [
    { value: 'grow_room', label: 'Grow Room' },
    { value: 'greenhouse', label: 'Greenhouse' },
    { value: 'outdoor_field', label: 'Outdoor Field' },
    { value: 'storage', label: 'Storage Area' },
    { value: 'processing', label: 'Processing Room' }
  ];

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border agricultural-shadow p-8">
        <div className="flex items-center justify-center space-x-3">
          <Icon name="Loader2" size={24} className="animate-spin text-primary" />
          <span className="text-muted-foreground">Loading team schedule...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow">
      {/* Show premium gate banner if not premium */}
      {needsUpgrade && (
        <div className="p-4 bg-primary/10 border-b border-primary/20">
          <div className="flex items-start space-x-3">
            <Icon name="Lock" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-primary">Preview Mode - Upgrade to Unlock Full Team Scheduling</p>
              <p className="text-xs text-muted-foreground mt-1">
                You're viewing example data. Upgrade to premium to manage real team schedules, reassign tasks, and create new assignments.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Team Schedule & Task Assignment</h3>
          <div className="flex items-center space-x-2">
            {hasPremium && (
              <Button
                size="sm"
                onClick={handleCreateNewTask}
                className="flex items-center space-x-2"
              >
                <Icon name="Plus" size={16} />
                <span>New Task</span>
              </Button>
            )}
            
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e?.target?.value)}
              className="px-3 py-1 border border-border rounded-md text-sm bg-background"
            >
              <option value="day">Day View</option>
              <option value="week">Week View</option>
              <option value="month">Month View</option>
            </select>
            
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e?.target?.value)}
              className="px-3 py-1 border border-border rounded-md text-sm bg-background"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <span className="text-destructive text-sm">{error}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setError('')}
                className="ml-auto"
              >
                <Icon name="X" size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* Time Header */}
        <div className="flex items-center mb-4">
          <div className="w-48 flex-shrink-0"></div>
          <div className="flex-1 grid grid-cols-10 gap-0 border-l border-border">
            {timeSlots?.map((time) => (
              <div key={time} className="px-2 py-1 text-xs text-muted-foreground border-r border-border text-center">
                {time}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
        {teamTasks?.map((member) => (
          <div 
            key={member?.id} 
            className="flex items-start"
            onDragOver={handleMemberDragOver}
            onDrop={(e) => handleMemberDrop(e, member?.id)}
          >
            {/* Member Info */}
            <div className="w-48 flex-shrink-0 flex items-center space-x-3 pr-4">
              <img
                src={member?.avatar}
                alt={member?.avatarAlt}
                className="w-8 h-8 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-medium text-foreground">{member?.member}</p>
                <p className="text-xs text-muted-foreground">{member?.position || `${member?.tasks?.length} tasks`}</p>
              </div>
            </div>

            {/* Timeline */}
            <div className="flex-1 relative h-16 border-l border-border">
              <div className="absolute inset-0 grid grid-cols-10 gap-0">
                {timeSlots?.map((_, index) => (
                  <div key={index} className="border-r border-border/30 h-full"></div>
                ))}
              </div>
              
              {/* Tasks */}
              {member?.tasks?.map((task) => {
                const position = calculateTaskPosition(task?.startTime, task?.endTime);
                return (
                  <div
                    key={task?.id}
                    draggable={hasPremium}
                    onDragStart={(e) => handleTaskDragStart(e, task, member?.id)}
                    onDragEnd={handleTaskDragEnd}
                    onClick={() => handleTaskClick(task, member?.id)}
                    className={`absolute top-1 h-14 rounded px-2 py-1 ${getStatusColor(task?.status)} ${hasPremium ? 'cursor-move hover:opacity-80' : 'cursor-default'} transition-all duration-200 ${hasPremium ? 'hover:scale-105' : ''}`}
                    style={position}
                    title={hasPremium 
                      ? `${task?.title} - ${task?.location} (${task?.startTime} - ${task?.endTime}) - Click to edit`
                      : `${task?.title} - ${task?.location} (${task?.startTime} - ${task?.endTime}) - Upgrade to edit`
                    }
                  >
                    <div className="flex items-center space-x-1 mb-1">
                      <Icon name={getPriorityIcon(task?.priority)} size={12} />
                      <span className="text-xs font-medium truncate">{task?.title}</span>
                    </div>
                    <p className="text-xs opacity-80 truncate">{task?.location}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="px-6 py-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-foreground">Status:</span>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded bg-success"></div>
                <span className="text-xs text-muted-foreground">Completed</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded bg-warning"></div>
                <span className="text-xs text-muted-foreground">In Progress</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded bg-primary"></div>
                <span className="text-xs text-muted-foreground">Scheduled</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded bg-error"></div>
                <span className="text-xs text-muted-foreground">Pending</span>
              </div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {hasPremium 
              ? 'Drag tasks between team members • Click to edit details • Drop to reassign' :'Viewing example schedule • Upgrade to manage real team tasks'
            }
          </div>
        </div>
      </div>

      {/* Task Edit/Create Modal - Only for premium users */}
      {showTaskModal && hasPremium && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg border border-border max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-foreground">
                {editingTask ? 'Edit Task' : 'Create New Task'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTaskModal(false)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Task Title */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Task Title</label>
                <Input
                  value={editForm?.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e?.target?.value }))}
                  placeholder="Enter task title..."
                />
              </div>

              {/* Assigned To */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Assign To</label>
                <Select
                  options={teamMembers?.map(member => ({
                    value: member?.userId,
                    label: member?.userProfile?.fullName || member?.userProfile?.email
                  }))}
                  value={editForm?.assignedTo}
                  onChange={(value) => setEditForm(prev => ({ ...prev, assignedTo: value }))}
                />
              </div>

              {/* Location Name */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Location Name</label>
                <Input
                  value={editForm?.locationName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, locationName: e?.target?.value }))}
                  placeholder="e.g., Sarah's Garden Plot, East Growing Zone..."
                />
              </div>

              {/* Room Type */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Room Type</label>
                <Select
                  options={roomTypeOptions}
                  value={editForm?.roomType}
                  onChange={(value) => setEditForm(prev => ({ ...prev, roomType: value }))}
                />
              </div>

              {/* Time Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Start Time</label>
                  <Input
                    type="time"
                    value={editForm?.startTime}
                    onChange={(e) => setEditForm(prev => ({ ...prev, startTime: e?.target?.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">End Time</label>
                  <Input
                    type="time"
                    value={editForm?.endTime}
                    onChange={(e) => setEditForm(prev => ({ ...prev, endTime: e?.target?.value }))}
                  />
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Priority</label>
                <Select
                  options={[
                    { value: 'low', label: 'Low' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'high', label: 'High' }
                  ]}
                  value={editForm?.priority}
                  onChange={(value) => setEditForm(prev => ({ ...prev, priority: value }))}
                />
              </div>
            </div>

            <div className="flex justify-between p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setShowTaskModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={editingTask ? handleSaveTask : handleCreateTask}
                disabled={!editForm?.title || !editForm?.assignedTo}
              >
                {editingTask ? 'Save Changes' : 'Create Task'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamScheduleGantt;